<template>
  <div
    class="show-for-sr"
    role="status"
    aria-live="polite"
  >
    {{ facetStatusMessage }}
  </div>
  <div
    v-if="facetOptions.length"
    class="grid-x facet-container"
  >
    <template
      v-for="facet in facetOptions"
      :key="facet.id"
    >
      <div class="csg-horizontal-facet-cont">
        <button
          :id="facet.id"
          :ref="`facet-button-${facet.id}`"
          :name="name"
          :aria-labelledby="`facet-label-${facet.id}`"
          :aria-expanded="facet.dropdown ? facet.dropdown?.isOpened ? true : false : undefined"
          :class="[
            {
              selected: facet.outlined,
            },
            `csg-horizontal-facet${facetStyle === 'chip' ? '-separator' : ''}`,
          ]"
          @keydown.enter.prevent="onClick(facet)"
          @keydown.space.prevent="onClick(facet)"
          @keydown.down.prevent="handleArrowKey(facet, 'down')"
          @keydown.esc.prevent="handleDropdownEsc(facet)"
          @click="onClick(facet)"
        >
          <span
            v-if="facet.contents.icon"
            :class="[facet.contents.icon, {'facet-trailing-icon' : facet.contents?.isTrailingIcon}]"
            :aria-label="facet.contents['aria-label']"
          />
          <span v-if="facet.contents.text">{{ facet.contents.text }}</span>
          <span v-if="facet.contents.number">{{ facet.contents.number }}</span>
          <div
            v-if="facet.closeable"
            class="close-icon-wrapper"
          >
            <Icon
              aria-label="close"
              size="s"
              :icon="csgClose"
            />
          </div>
          <Icon
            v-if="facet.dropdown"
            size="s"
            :class="`${facet.dropdown?.isOpened ? 'up-icon' : 'down-icon'}`"
            :icon="facet.dropdown?.isOpened ? csgChevronLargeUp : csgChevronLargeDown"
          />
        </button>
        <template v-if="facet.dropdown">
          <div class="facet-dropdown-cont">
            <div
              class="dropdown"
              :ref="`facet-dropdown-${facet.id}`"
              :class="{ opened: facet.dropdown.isOpened }"
              @focusout="handleFocusOut(facet, $event)"
              @keydown="handleDropdownKeydown(facet, $event)"
              @keydown.esc.prevent="handleDropdownEsc(facet)"
            >
              <component
                :key="facet"
                :is="facet.dropdown.customComponent"
                v-if="facet.dropdown.customComponent"
              />
            </div>
          </div>
        </template>
      </div>
    </template>
  </div>
</template>

<script>
import { csgClose, csgChevronLargeDown, csgChevronLargeUp } from '@csgSvgIcons/icons';
import { getFocusableElements } from '../../utils/focusTrap';
import Icon from '../icon';

export default {
  name: 'HorizontalFacets',
  components: {
    Icon,
  },
  props: {
    name: {
      type: String,
      default: 'facet',
    },
    facetStyle: {
      type: String,
      default: '',
    },
    disableAutoOutlined: {
      type: Boolean,
      default: false,
    },
    options: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['onFacetClick', 'onCloseFacetDropdown'],
  data() {
    return {
      facetOptions: [...this.options],
      facetStatusMessage: '',
      csgChevronLargeDown,
      csgChevronLargeUp,
      csgClose,
    };
  },
  watch: {
    options(newOptions) {
      this.facetOptions = [...newOptions];
    },
  },
  methods: {
    dropdownEventHandler(facet) {
      // handles listeners for toggling dropdown
      const documentListener = (e) => {
        const includesButtonElement = e.composedPath().includes(this.$refs[`facet-button-${facet.id}`][0]);
        const includesFacetDropdown = e.composedPath().includes(this.$refs[`facet-dropdown-${facet.id}`][0]);
        if (!facet.dropdown.isOpened && includesButtonElement) {
          // when clicking on same dropdown button, emit event on close and remove listeners
          this.$emit('onCloseFacetDropdown', facet);
          document.removeEventListener('click', documentListener, false);
          document.removeEventListener('keydown', documentListener, false);
        } else if (!includesFacetDropdown && !includesButtonElement) {
          // when clicking on page outside of dropdown and button, set isOpened to false, emit event and remove listeners
          facet.dropdown.isOpened = false;
          this.$emit('onCloseFacetDropdown', facet);
          document.removeEventListener('click', documentListener, false);
          document.removeEventListener('keydown', documentListener, false);
        }
      };
      // only add listeners when facet dropdown is opened
      if (facet.dropdown.isOpened) {
        document.addEventListener('click', documentListener, false);
        document.addEventListener('keydown', documentListener, false);
      }
    },
    async onClick(facet) {
      if (facet.closeable) {
        const selectedFacet = this.facetOptions.findIndex((val) => facet.id === val.id);
        this.facetOptions.splice(selectedFacet, 1);
        this.$emit('onFacetClick', { selected: facet });
        this.facetStatusMessage = `${facet.contents.text} facet removed`;
        return;
      }
      if (facet.dropdown) {
        facet.dropdown.isOpened = !facet.dropdown.isOpened;
        if (facet.dropdown.isOpened) {
          await this.$nextTick(() => {
            this.$refs[`facet-button-${facet.id}`][0]?.addEventListener('keydown', (el) => {
              const focusableEl = getFocusableElements(this.$refs[`facet-dropdown-${facet.id}`][0]);
              if (el.key === 'Tab' && !el.shiftKey && focusableEl.length > 0) {
                el.preventDefault();
                focusableEl[0].focus();
              }
            });
          });
        }
        this.dropdownEventHandler(facet);
      } else if (!this.disableAutoOutlined) {
        facet.outlined = !facet.outlined;
      }
      this.$emit('onFacetClick', {
        selected: facet,
        dropdownRef: facet.dropdown ? this.$refs[`facet-dropdown-${facet.id}`][0] : null,
      });
    },
    handleFocusOut(facet, event) {
      const newFocusTarget = event.relatedTarget || event.target;
      const dropdown = this.$refs[`facet-dropdown-${facet.id}`][0];
      const button = this.$refs[`facet-button-${facet.id}`][0];

      if (
        !dropdown.contains(newFocusTarget)
        && !button.contains(newFocusTarget)
      ) {
        facet.dropdown.isOpened = false;
        this.$emit('onCloseFacetDropdown', facet);
      }
    },
    handleDropdownEsc(facet) {
      facet.dropdown.isOpened = false;
      this.$emit('onCloseFacetDropdown', facet);
      this.$refs[`facet-button-${facet.id}`][0].focus();
    },
    handleArrowKey(facet, direction) {
      if (facet.dropdown?.isOpened) {
        const focusableEl = getFocusableElements(
          this.$refs[`facet-dropdown-${facet.id}`][0],
        );
        if (focusableEl.length > 0) {
          if (direction === 'down') {
            focusableEl[0].focus();
          } else {
            focusableEl[focusableEl.length - 1].focus();
          }
        }
      }
    },
    handleDropdownKeydown(facet, event) {
      const focusableEl = getFocusableElements(
        this.$refs[`facet-dropdown-${facet.id}`][0],
      );
      const currentIndex = focusableEl.indexOf(document.activeElement);

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        const nextIndex = (currentIndex + 1) % focusableEl.length;
        focusableEl[nextIndex].focus();
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        const prevIndex = (currentIndex - 1 + focusableEl.length) % focusableEl.length;
        focusableEl[prevIndex].focus();
      } else if (event.key === ' ') {
        event.preventDefault();
        const { activeElement } = document;
        if (focusableEl.includes(activeElement)) {
          activeElement.click();
        } else {
          this.onClick(facet);
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~@core/csg/src/scss/facets/facets.scss";

@include csg-facets;

.close-icon-wrapper {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.facet-dropdown-cont {
  position: absolute;
  display: inline-block;
}

.csg-horizontal-facet {
  margin-right: rem-calc(16);
  display: flex;
  vertical-align: top;
  position: relative;
  @include breakpoint(small down) {
    margin-right: rem-calc(12);
  }
}

.csg-horizontal-facet-separator {
  display: inline-flex;
}

.facet-container {
  display: flex;
  flex-flow: row wrap;
  row-gap: rem-calc(16);
  @include breakpoint(small down) {
    overflow-x: scroll;
    overflow-y: hidden;
    white-space: nowrap;
    flex-flow: nowrap;
    &::-webkit-scrollbar {
      display: none;
    }
  }
}
.facet-trailing-icon {
  order: 2;
}
.dropdown {
  border: $gray-2-border;
  border-radius: rem-calc(5);
  padding: rem-calc(24 16);
  display: none;
  position: absolute;
  background: $white;
  z-index: 3; // it has to be lower than <header> z-index on actual page
  margin-top: rem-calc(8);
  white-space: nowrap;

  &.opened {
    display: block;
  }
}
// When vue fragment is available, the div wrapper can be created only for non-chip facet,
// so that we can keep CSG facet
.csg-horizontal-facet-cont {
  .csg-horizontal-facet-separator {
    border-width: 1px;
    border-style: solid;
    border-color: $horizontal-facet-separator-border-color;
  }
  &:last-of-type {
    .csg-horizontal-facet-separator {
      border: none;
    }
  }
}
</style>
